import type { Nullable } from '@iheartjane/types';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { PropsWithChildren } from 'react';
import { createContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { CheckoutLocationState } from '@jane/shared-ecomm/providers';

import { useCheckoutInfoIsValid } from '../util/hooks/useCheckoutInfoIsValid';
import { useCheckoutOptionsIsValid } from '../util/hooks/useCheckoutOptionsIsValid';
import { getDefaultExpanded } from './getDefaultExpanded';
import { isReturningFromAuth } from './isReturningFromAuth';

export type AccordionItems = 'options' | 'info' | 'payments';

interface CheckoutAccordionContextValue {
  expandableItems: AccordionItems[];
  expanded: AccordionItems;
  setExpanded: (items: AccordionItems) => void;
}

const CheckoutAccordionContext =
  createContext<CheckoutAccordionContextValue | null>(null);
CheckoutAccordionContext.displayName = 'CheckoutAccordion';

export const useCheckoutAccordion = () =>
  useContextEnsuringProvider(CheckoutAccordionContext);

export const CheckoutAccordionProvider = ({ children }: PropsWithChildren) => {
  const state: Nullable<CheckoutLocationState> = useLocation().state;
  const justSignedIn = isReturningFromAuth(state);

  const { docsValid, formValid } = useCheckoutInfoIsValid();
  const infoValid = docsValid && formValid;

  const optionsValid = useCheckoutOptionsIsValid();

  const expandableItems: AccordionItems[] = useMemo(() => {
    return [
      'options',
      ...(optionsValid ? ['info' as const] : []),
      ...(optionsValid && infoValid ? ['payments' as const] : []),
    ];
  }, [optionsValid, infoValid]);

  const [expanded, setExpanded] = useState<AccordionItems>(
    justSignedIn
      ? getDefaultExpanded({
          infoValid,
          optionsValid: !!optionsValid,
        })
      : 'options'
  );

  const accordionContextValues = useMemo(
    () => ({
      expandableItems,
      expanded,
      setExpanded,
    }),
    [expandableItems, expanded]
  );

  return (
    <CheckoutAccordionContext.Provider value={accordionContextValues}>
      {children}
    </CheckoutAccordionContext.Provider>
  );
};
